"use client"
// @ts-nocheck — third-party hook return types not fully typed
import { Badge } from "@repo/ui/components/ui/badge"
import { Button } from "@repo/ui/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@repo/ui/components/ui/card"
import { ScrollArea } from "@repo/ui/components/ui/scroll-area"
import { Separator } from "@repo/ui/components/ui/separator"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@repo/ui/components/ui/tabs"
import { cn } from "@repo/ui/lib/utils"
import { usePublicSubscriptionConfig } from "@repo/ui/hooks/api/settings"
import { motion, useInView } from "framer-motion"
import { Check, Gift, Sparkles } from "lucide-react"
import { resolvePlanFeature } from "@repo/ui/lib/plan-feature-adapter"
import { useEffect, useMemo, useRef, useState } from "react"
import {
  FREE_TRIAL_SECTION_ID,
  PRICING_HASH_ADVANCED,
  PRICING_HASH_DENDROMAP,
  PRICING_SECTION_ID,
  registerPricingTabNavigate,
  resolvePricingTabFromHash,
  scrollToPricingSection,
  type PricingTabValue,
} from "./pricing-anchors"
import { SignupDialog, type SelectedTier, type SelectedPlanType } from "./signup-dialog"

interface PricingSectionProps {
  content: Record<string, string>
}

const FREE_PLAN_CHIPS = ["1 parcours", "1 mois", "Sans CB", "App mobile"]

const PRICING_TAB_LOGOS = {
  basic: "/images/logos/dendromap.svg",
  advanced: "/images/logos/dendro-advanced.svg",
} as const

// ─── Tab trigger ──────────────────────────────────────────────────────────────

interface PricingTabTriggerProps {
  value: "basic" | "advanced"
  logoSrc: string
  label: string
  hint: string
}

function PricingTabTrigger({ value, logoSrc, label, hint }: PricingTabTriggerProps): JSX.Element {
  return (
    <TabsTrigger
      value={value}
      className={cn(
        "flex h-auto min-h-[7rem] w-full flex-col items-center justify-center gap-2 rounded-xl border-2 px-4 py-4",
        "bg-card/80 text-foreground shadow-sm transition-all duration-200",
        "border-border/70 hover:border-primary/35 hover:bg-card",
        "data-[state=active]:border-primary data-[state=active]:bg-background data-[state=active]:shadow-forest",
        "data-[state=active]:ring-2 data-[state=active]:ring-primary/15",
        "[&_img]:pointer-events-none [&_img]:h-11 [&_img]:w-full [&_img]:max-w-[15rem] [&_img]:select-none [&_img]:object-contain",
        "md:[&_img]:h-[150px] md:[&_img]:max-w-[18rem]",
        "[&_img]:opacity-60 [&_img]:transition-opacity",
        "data-[state=active]:[&_img]:opacity-100",
        "hover:[&_img]:opacity-80",
      )}
    >
      <div className="flex h-20 w-full items-center justify-center md:h-16">
        <img src={logoSrc} alt="" aria-hidden />
      </div>
      <span className="sr-only">{label}</span>
      <span className="text-center text-xs leading-snug text-muted-foreground">
        {hint}
      </span>
    </TabsTrigger>
  )
}

// ─── Plan card ────────────────────────────────────────────────────────────────

interface PricingPlanCardData {
  name: string
  popular?: boolean
  price: string
  period: string
  description: string
  features: string[]
  id?: string
}

interface PricingPlanCardProps {
  plan: PricingPlanCardData
  index: number
  isInView: boolean
  onSelect: () => void
}

function PricingPlanCard({ plan, index, isInView, onSelect }: PricingPlanCardProps): JSX.Element {
  const priceAmount = String(plan.price).replace("€", "")

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.45, delay: index * 0.08, ease: "easeOut" }}
      className="h-full"
    >
      <Card
        className={cn(
          "relative flex h-full flex-col transition-all duration-200 hover:-translate-y-1",
          plan.popular
            ? "border-primary shadow-forest-lg ring-1 ring-primary/20"
            : "border-border hover:border-primary/25 hover:shadow-forest",
        )}
      >
        {/* Popular badge — gradient pill */}
        {plan.popular && (
          <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-white shadow-md"
              style={{
                background: "linear-gradient(135deg, #16a34a 0%, #22c55e 100%)",
              }}
            >
              <Sparkles className="size-3" aria-hidden />
              Plus populaire
            </span>
          </div>
        )}

        <CardHeader className={cn("items-center gap-3 pb-2 text-center", plan.popular && "pt-7")}>
          <CardTitle className="text-lg font-semibold tracking-tight">{plan.name}</CardTitle>
          <CardDescription className="text-sm font-medium text-foreground">
            {plan.description}
          </CardDescription>
          <div className="flex flex-col items-center gap-0.5 pt-1">
            <div className="flex items-baseline justify-center gap-0.5">
              <span className="text-4xl font-bold tracking-tight text-foreground">
                {priceAmount}
              </span>
              <span className="text-lg font-bold text-foreground">€</span>
              <span className="text-xs text-muted-foreground ml-1">{plan.period}</span>
            </div>
            <p className="text-xs text-muted-foreground">Forfait mensuel · HT</p>
          </div>
        </CardHeader>

        <Separator />

        <CardContent className="flex flex-1 flex-col gap-4 pt-4">
          <ScrollArea className="h-44 pr-3">
            <ul className="flex flex-col gap-2">
              {plan.features.map((key: string) => {
                const { label } = resolvePlanFeature(key)
                return (
                  <li key={key} className="flex items-start gap-2 text-sm leading-snug">
                    <Check className="mt-0.5 size-3.5 shrink-0 text-primary" aria-hidden />
                    <span className="text-muted-foreground">{label}</span>
                  </li>
                )
              })}
            </ul>
          </ScrollArea>
          <Button
            className="mt-auto w-full hover-shimmer"
            size="lg"
            variant={plan.popular ? "default" : "secondary"}
            onClick={onSelect}
          >
            Choisir ce forfait
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  )
}

// ─── Free tier banner ─────────────────────────────────────────────────────────

interface FreeTierBannerProps {
  isInView: boolean
  onStartFree: () => void
}

function FreeTierBanner({ isInView, onStartFree }: FreeTierBannerProps): JSX.Element {
  return (
    <motion.div
      id={FREE_TRIAL_SECTION_ID}
      className="mb-2 scroll-mt-24"
      initial={{ opacity: 0, y: 16 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
      transition={{ duration: 0.5, delay: 0.1 }}
    >
      <div className="rounded-2xl border border-primary/20 bg-gradient-to-r from-primary/6 via-primary/3 to-transparent p-4 md:p-5 shadow-forest">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-3 md:items-center">
            <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary/12">
              <Gift className="size-5 text-primary" aria-hidden />
            </div>
            <div className="min-w-0">
              <p className="text-base font-semibold text-foreground md:text-lg">
                Essai gratuit · 1 parcours
              </p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Testez Dendromap 1 mois, sans carte bancaire ni engagement
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5 md:hidden">
                {FREE_PLAN_CHIPS.map((chip) => (
                  <Badge key={chip} variant="secondary" className="text-xs font-normal">
                    {chip}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end md:shrink-0">
            <div className="hidden flex-wrap justify-end gap-1.5 md:flex">
              {FREE_PLAN_CHIPS.map((chip) => (
                <Badge key={chip} variant="secondary" className="text-xs font-normal">
                  {chip}
                </Badge>
              ))}
            </div>
            <Button
              className="h-10 w-full shrink-0 bg-primary px-6 hover:bg-primary/90 sm:w-auto hover-shimmer"
              onClick={onStartFree}
            >
              Démarrer un essai gratuit
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

// ─── Custom plan footer ───────────────────────────────────────────────────────

interface CustomPlanFooterProps {
  isInView: boolean
  message: string
}

function CustomPlanFooter({ isInView, message }: CustomPlanFooterProps): JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
      transition={{ duration: 0.4, delay: 0.1 }}
      className="flex flex-col items-center gap-4 border-t border-border pt-8 text-center"
    >
      <p className="max-w-2xl text-base leading-relaxed md:text-lg">
        <span className="font-semibold text-primary">ENCORE +</span>{" "}
        <span className="text-muted-foreground">{message}</span>
      </p>
      <Button variant="outline" className="hover-shimmer" asChild>
        <a href="mailto:contact@dendromap.fr">
          Nous contacter — contact@dendromap.fr
        </a>
      </Button>
    </motion.div>
  )
}

// ─── Main section ─────────────────────────────────────────────────────────────

export function PricingSection({ content }: PricingSectionProps): JSX.Element {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  const { data: subscription } = usePublicSubscriptionConfig()

  const [isOpen, setIsOpen] = useState(false)
  const [isFreeMode, setIsFreeMode] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<SelectedPlanType>("BASIC")
  const [selectedTier, setSelectedTier] = useState<SelectedTier | null>(null)
  const [pricingTab, setPricingTab] = useState<PricingTabValue>("basic")

  // Sync pricing tab with URL hash (back/forward, liens ancres directs)
  useEffect(() => {
    function syncFromUrl(shouldScroll: boolean): void {
      const tab = resolvePricingTabFromHash(window.location.hash)
      if (!tab) return
      setPricingTab(tab)
      if (shouldScroll) {
        requestAnimationFrame(() => scrollToPricingSection())
      }
    }

    registerPricingTabNavigate(setPricingTab)
    syncFromUrl(true)

    const onHashOrPopState = (): void => syncFromUrl(true)
    window.addEventListener("hashchange", onHashOrPopState)
    window.addEventListener("popstate", onHashOrPopState)

    return () => {
      registerPricingTabNavigate(null)
      window.removeEventListener("hashchange", onHashOrPopState)
      window.removeEventListener("popstate", onHashOrPopState)
    }
  }, [])

  function handlePricingTabChange(value: string) {
    const tab = value as PricingTabValue
    setPricingTab(tab)
    const hash = tab === "advanced" ? PRICING_HASH_ADVANCED : PRICING_HASH_DENDROMAP
    const nextHash = `#${hash}`
    if (window.location.hash !== nextHash) {
      window.history.replaceState(null, "", nextHash)
    }
  }

  function openFreeSignupDialog() {
    setSelectedPlan("BASIC")
    setSelectedTier(null)
    setIsFreeMode(true)
    setIsOpen(true)
  }

  function openPaidSignupDialog(plan: SelectedPlanType, tier: SelectedTier) {
    setIsFreeMode(false)
    setSelectedPlan(plan)
    setSelectedTier(tier)
    setIsOpen(true)
  }

  function closeSignupDialog() {
    setIsOpen(false)
    setIsFreeMode(false)
  }

  // Map API data to UI card structure
  const { basicCards, advancedCards } = useMemo(() => {
    if (!subscription) return { basicCards: [], advancedCards: [] }
    const basic = subscription.plans.BASIC
    const advanced = subscription.plans.ADVANCED
    const fmt = (v: number) => `${v}€`

    const basicCards = basic.tiers.map((t: any, idx: number) => ({
      name: t.title || (idx === 0 ? `1 à ${t.maxTrees} arbres` : `Jusqu'à ${t.maxTrees} arbres`),
      popular: idx === 1,
      price: fmt(t.price),
      priceValue: t.price,
      period: "/mois",
      description: t.subTitle || "Forfait mensuel",
      features: t.benefits,
      id: t.id,
    }))

    const advancedCards = advanced.tiers.map((t: any) => ({
      name: t.title || `Jusqu'à ${t.maxTrees} arbres`,
      popular: false,
      price: fmt(t.price),
      priceValue: t.price,
      period: "/mois",
      description: t.subTitle || "Forfait mensuel",
      features: t.benefits,
      id: t.id,
    }))

    return { basicCards, advancedCards }
  }, [subscription])

  return (
    <section id={PRICING_SECTION_ID} className="scroll-mt-24 bg-muted py-16 sm:py-24" ref={ref}>
      <motion.div className="container mx-auto flex flex-col gap-10 px-4 sm:px-6 lg:gap-12 lg:px-8">

        {/* Section header */}
        <motion.div
          className="mx-auto flex max-w-3xl flex-col gap-4 text-center"
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
          transition={{ duration: 0.5 }}
        >
          <Badge variant="secondary" className="mx-auto w-fit">
            Abonnements
          </Badge>
          <h2 className="text-4xl font-semibold tracking-tight text-foreground md:text-5xl">
            {content["pricing.title"] || "Tarifs"}{" "}
            <span className="text-primary">{content["pricing.subtitle"] || "Flexibles"}</span>
          </h2>
          <p className="text-base text-muted-foreground md:text-lg">
            {content["pricing.description"] || "Choisissez le plan qui correspond à vos besoins"}
          </p>
        </motion.div>

        <FreeTierBanner isInView={isInView} onStartFree={openFreeSignupDialog} />

        <Tabs value={pricingTab} onValueChange={handlePricingTabChange} className="flex flex-col gap-8">
          <TabsList className="mx-auto grid h-auto w-full max-w-2xl grid-cols-1 gap-3 bg-transparent p-0 sm:grid-cols-2">
            <PricingTabTrigger
              value="basic"
              logoSrc={PRICING_TAB_LOGOS.basic}
              label={content["pricing.tabs.professionals"] || "Dendromap"}
              hint="Inventaire arbres — professionnels & petites structures"
            />
            <PricingTabTrigger
              value="advanced"
              logoSrc={PRICING_TAB_LOGOS.advanced}
              label={content["pricing.tabs.collectivites"] || "Advanced+"}
              hint="Arbres, alignements & espaces boisés — collectivités"
            />
          </TabsList>

          <TabsContent value="basic" className="mt-0 outline-none">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
              {basicCards.map((plan: PricingPlanCardData, index: number) => (
                <PricingPlanCard
                  key={plan.id ?? `basic-${index}`}
                  plan={plan}
                  index={index}
                  isInView={isInView}
                  onSelect={() => openPaidSignupDialog("BASIC", plan as SelectedTier)}
                />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="advanced" className="mt-0 outline-none">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
              {advancedCards.map((plan: PricingPlanCardData, index: number) => (
                <PricingPlanCard
                  key={plan.id ?? `advanced-${index}`}
                  plan={plan}
                  index={index}
                  isInView={isInView}
                  onSelect={() => openPaidSignupDialog("ADVANCED", plan as SelectedTier)}
                />
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <CustomPlanFooter
          isInView={isInView}
          message={
            content["pricing.custom.message"] ||
            "Besoins spécifiques ? Nous vous proposons également de créer votre application sur mesure."
          }
        />
      </motion.div>

      {/* Extracted signup dialog */}
      <SignupDialog
        open={isOpen}
        isFreeMode={isFreeMode}
        selectedPlan={selectedPlan}
        selectedTier={selectedTier}
        onClose={closeSignupDialog}
      />
    </section>
  )
}
